import { ComponentPropsWithoutRef, ReactNode, useMemo } from 'react'
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import groupBy from 'lodash/groupBy'
import { comparisonOperatorConfigsByColType } from '@global/constants'
import { ColumnDataType } from '@global/types'
import { MenuItemDivider } from './MenuItemDivider'
import { t } from '@lingui/core/macro'
import { i18n } from '@lingui/core'

type FormControlProps = ComponentPropsWithoutRef<typeof FormControl>

interface Props extends FormControlProps {
  columnDataType: ColumnDataType
  FormControlComponent: React.ElementType
  name?: string
  value: string
  onChangeSelect: (
    event: import('@mui/material').SelectChangeEvent<string>,
    child: ReactNode,
  ) => void
  operatorFilter: (config) => boolean
}

export const ComparisonOperatorSelect = ({
  columnDataType = ColumnDataType.DEFAULT,
  FormControlComponent = FormControl,
  name = 'operator',
  value,
  onChangeSelect,
  operatorFilter = () => true,
  ...props
}: Props) => {
  const comparisonOperatorConfigs = comparisonOperatorConfigsByColType[columnDataType]

  const operatorConfigGroups = useMemo(() => {
    const filteredOperatorConfigs = Object.values(comparisonOperatorConfigs).filter(
      operatorConfig => operatorFilter(operatorConfig),
    )

    return Object.values(groupBy(filteredOperatorConfigs, 'group'))
  }, [operatorFilter])

  return (
    <FormControlComponent {...props}>
      <InputLabel id="operator-select-label">{t`Operator`}</InputLabel>
      <Select
        labelId="operator-select-label"
        label="Operator"
        name={name}
        value={value}
        onChange={onChangeSelect}
        data-testid="mui-select"
      >
        <MenuItem value="" data-testid="select-menu-item-none">
          <em>{t`None`}</em>
        </MenuItem>
        {operatorConfigGroups.map(operatorConfigGroup => [
          <MenuItemDivider key="divider" variant="middle" />,
          operatorConfigGroup.map(operatorConfig => (
            <MenuItem
              key={operatorConfig.operator}
              value={operatorConfig.operator}
              data-testid="select-menu-item"
            >
              {i18n._(operatorConfig.description)}
            </MenuItem>
          )),
        ])}
      </Select>
    </FormControlComponent>
  )
}
